import {createCadEntityIdService} from '../../document/CadEntityIdService';
import type {CadEntityKind} from '../../document/CadEntityTypes';

/**
 * 草图持久身份工厂。
 *
 * 设计说明：
 * 1. 草图组、草图实体（点/边/槽口/集合）与几何关系的跨会话稳定 ID 统一由本模块分配，
 *    格式复用 Phase 0 文档层 `CadEntityIdService`（`cad:<kind>:<uuid>`），
 *    与 Three.js 运行时 `uuid` 严格区分；
 * 2. ID 一经分配即随对象生命周期保留：`cloneSelf` 系（历史快照/恢复）表示同一逻辑实体，
 *    必须继承；复制出“新逻辑实体”的流程（设计树复制草图、复制实体）必须显式重新生成；
 * 3. 该服务纯函数无状态，不持有任何全局可变数据。
 */
const sketchPersistentIdService = createCadEntityIdService();

/**
 * 创建草图组持久 ID。
 *
 * @returns `cad:sketch:<uuid>` 格式的稳定 ID。
 */
export function createSketchPersistentId(): string {
    return sketchPersistentIdService.create({kind: 'sketch'});
}

/**
 * 创建草图实体（点/边/槽口/集合）持久 ID。
 *
 * @returns `cad:sketch-entity:<uuid>` 格式的稳定 ID。
 */
export function createSketchEntityPersistentId(): string {
    return sketchPersistentIdService.create({kind: 'sketch-entity'});
}

/**
 * 创建草图几何关系持久 ID。
 *
 * @returns `cad:sketch-constraint:<uuid>` 格式的稳定 ID。
 */
export function createSketchConstraintPersistentId(): string {
    return sketchPersistentIdService.create({kind: 'sketch-constraint'});
}

/**
 * 创建草图尺寸持久 ID。
 *
 * @returns `cad:sketch-dimension:<uuid>` 格式的稳定 ID。
 */
export function createSketchDimensionPersistentId(): string {
    return sketchPersistentIdService.create({kind: 'sketch-dimension'});
}

/**
 * 断言字符串是指定种类的草图持久 ID。
 *
 * @param persistentId 待校验的持久 ID。
 * @param expectedKind 期望的 CAD 实体种类。
 * @param label 错误消息中的字段名称。
 * @returns 校验通过的原始持久 ID。
 */
function assertSketchPersistentIdKind(
    persistentId: string,
    expectedKind: CadEntityKind,
    label: string,
): string {
    const parsed = sketchPersistentIdService.parse(persistentId);

    if (parsed?.kind !== expectedKind) {
        throw new Error(`${label} 必须是 ${expectedKind} 持久 ID：${persistentId}`);
    }

    return persistentId;
}

/**
 * 断言字符串是草图组持久 ID。
 *
 * @param persistentId 待校验的持久 ID。
 * @param label 错误消息中的字段名称。
 * @returns 校验通过的原始持久 ID。
 */
export function assertSketchPersistentId(
    persistentId: string,
    label = '草图 ID',
): string {
    return assertSketchPersistentIdKind(persistentId, 'sketch', label);
}

/**
 * 断言字符串是草图实体持久 ID。
 *
 * @param persistentId 待校验的持久 ID。
 * @param label 错误消息中的字段名称。
 * @returns 校验通过的原始持久 ID。
 */
export function assertSketchEntityPersistentId(
    persistentId: string,
    label = '草图实体 ID',
): string {
    return assertSketchPersistentIdKind(persistentId, 'sketch-entity', label);
}
